import { useState } from 'react';
import { ResponseException, UserModel } from '../models';
import { AxiosPrivate, AxiosPublic } from '../utils/axios';
import { Utils, UserSubmissionType } from '@mvrb-frontend/shared-ui';
import { PaginationQuery } from '../interfaces/paginationQuery.interface';
import { updateCookie } from '../utils/auth';

const USERS_BASE_URL = 'users';

export const useUserApi = () => {
  const [isUserApiSubmitting, setUserApiSubmitting] = useState(false);
  const [isUserApiFetching, setUserApiFetching] = useState(false);
  const [userApiSuccess, setUserApiSuccess] = useState(false);
  const [userApiErrors, setUserApiErrors] = useState<string[]>([]);
  const [userSubmissionType, setUserSubmissionType] = useState<UserSubmissionType>();
  const [submittedUser, setSubmittedUser] = useState<UserModel | null>(null);
  const [fetchedUsers, setFetchedUsers] = useState<[UserModel?]>([]);
  const [fetchedUser, setFetchedUser] = useState<UserModel | null>(null);
  const [staffOptions, setStaffOptions] = useState([]);
  const [userCount, setUserCount] = useState(0);

  const submitUser = async (user: UserModel, submissionType: UserSubmissionType, organizations?: any) => {
    setUserSubmissionType(submissionType);
    const updatedSubmittingUser = user && Utils.nullifyEmptyStringProperties(user);
    try {
      setUserApiSubmitting(true);
      switch (submissionType) {
        case UserSubmissionType.UpdateByStaff:
          await AxiosPrivate.patch(`/${USERS_BASE_URL}/${updatedSubmittingUser.id}`, updatedSubmittingUser);
          break;
        case UserSubmissionType.UpdateOwn:
          updateCookie(updatedSubmittingUser);
          await AxiosPrivate.patch(`/${USERS_BASE_URL}/me`, updatedSubmittingUser);
          break;
        case UserSubmissionType.AddByStaff:
          // Setting an arbitrary password for the new user. User will later set a new password once login for the first time
          updatedSubmittingUser.password = 'MUk9brWcha4*';
          await AxiosPrivate.post(`/${USERS_BASE_URL}`, updatedSubmittingUser);
          break;
        case UserSubmissionType.Register:
          await AxiosPublic.post(`/${USERS_BASE_URL}/register`, updatedSubmittingUser);
          break;
        case UserSubmissionType.Delete:
          await AxiosPrivate.delete(`/${USERS_BASE_URL}/${updatedSubmittingUser.id}`);
          break;
      }
      setUserApiSuccess(true);
      setSubmittedUser(updatedSubmittingUser);
      setUserApiErrors([]);
    } catch (e) {
      setUserApiErrors(new ResponseException(e).getErrorMessage());
      setUserApiSuccess(false);
      setSubmittedUser(null);
    } finally {
      setUserApiSubmitting(false);
    }
  };

  const fetchUsers = async (queryParams?: PaginationQuery) => {
    try {
      setUserApiFetching(true);
      const { data } = await AxiosPrivate.get(`/${USERS_BASE_URL}`, { params: { ...queryParams, getUserCount: true } });
      const items = data.users
        .map(user => new UserModel(user))
        .sort((u1, u2) => {
          // Sorting users who have no organization but have community (member of public)
          if (u1.organizationIds.length || u2.organizationIds.length) {
            return 0;
          }
          return +Boolean(u1.community) - +Boolean(u2.community);
        });
      setFetchedUsers(items);
      setUserCount(data.count);
      setUserApiSuccess(true);
      setUserApiErrors([]);
    } catch (e) {
      setUserApiErrors(new ResponseException(e).getErrorMessage());
      setUserApiSuccess(false);
      setFetchedUsers([]);
    } finally {
      setUserApiFetching(false);
    }
  };

  const fetchStaffOptions = async () => {
    try {
      setUserApiFetching(true);
      const { data } = await AxiosPublic.get(`/${USERS_BASE_URL}/staff`);
      const staff = data.map(user => {
        return { label: user.displayName, value: user.id };
      });
      setStaffOptions(staff);
      setUserApiSuccess(true);
      setUserApiErrors([]);
    } catch (e) {
      setUserApiErrors(new ResponseException(e).getErrorMessage());
      setUserApiSuccess(false);
      setFetchedUsers([]);
    } finally {
      setUserApiFetching(false);
    }
  };

  const fetchUser = async id => {
    try {
      setUserApiFetching(true);
      const { data } = await AxiosPrivate.get(`/${USERS_BASE_URL}/${id}`);
      setFetchedUser(new UserModel(data));
      setUserApiSuccess(true);
      setUserApiErrors([]);
    } catch (e) {
      setUserApiErrors(new ResponseException(e).getErrorMessage());
      setUserApiSuccess(false);
      setFetchedUser(null);
    } finally {
      setUserApiFetching(false);
    }
  };

  const resetUserApi = () => {
    setUserApiSuccess(false);
    setUserApiErrors([]);
    setSubmittedUser(null);
  };

  return {
    isUserApiSubmitting,
    isUserApiFetching,
    userApiSuccess,
    userApiErrors,
    userSubmissionType,
    submittedUser,
    fetchedUsers,
    fetchedUser,
    userCount,
    submitUser,
    fetchUser,
    fetchUsers,
    resetUserApi,
    fetchStaffOptions,
    staffOptions,
  };
};
